import { useEffect, useState } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TrendingUp, Users, Clock, Award, Gift, BarChart3, Trophy } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

export default function Analytics() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardCategory, setLeaderboardCategory] = useState('hours_donated');

  useEffect(() => {
    fetchAnalytics();
    fetchLeaderboard('hours_donated');
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/personal`, { withCredentials: true });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Set empty analytics to stop infinite loading
      setAnalytics({
        hours_donated: 0,
        hours_received: 0,
        people_helped: 0,
        helpers_count: 0,
        credits_earned: 0,
        credits_spent: 0,
        current_balance: 0,
        monthly_trend: [],
        top_skills: [],
        total_reviews: 0,
        average_rating: 0,
        donations_made: 0,
        donations_received: 0
      });
      toast.error('Failed to load analytics');
    }
  };

  const fetchLeaderboard = async (category) => {
    try {
      const response = await axios.get(`${API}/analytics/leaderboard?category=${category}`, { withCredentials: true });
      setLeaderboard(response.data);
      setLeaderboardCategory(category);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  if (!analytics) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-8 w-8 text-teal-500" />
          <h1 className="text-3xl font-bold text-gray-900">{t('analytics.title')}</h1>
        </div>

        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="p-6 border-l-4 border-teal-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('analytics.hoursDonated')}</p>
                <p className="text-3xl font-bold text-teal-600 mt-2">{analytics.hours_donated}</p>
              </div>
              <Clock className="h-10 w-10 text-teal-500 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-cyan-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('analytics.hoursReceived')}</p>
                <p className="text-3xl font-bold text-cyan-600 mt-2">{analytics.hours_received}</p>
              </div>
              <Clock className="h-10 w-10 text-cyan-500 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('analytics.peopleHelped')}</p>
                <p className="text-3xl font-bold text-purple-600 mt-2">{analytics.people_helped}</p>
              </div>
              <Users className="h-10 w-10 text-purple-500 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('analytics.averageRating')}</p>
                <p className="text-3xl font-bold text-yellow-600 mt-2">{(analytics.average_rating || 0).toFixed(1)} ⭐</p>
              </div>
              <Award className="h-10 w-10 text-yellow-500 opacity-50" />
            </div>
          </Card>
        </div>

        {/* Credits Activity */}
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-6">{t('analytics.creditsActivity')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">{t('analytics.creditsEarned')}</p>
              <p className="text-3xl font-bold text-green-600">{analytics.credits_earned}</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">{t('analytics.creditsSpent')}</p>
              <p className="text-3xl font-bold text-red-600">{analytics.credits_spent}</p>
            </div>
            <div className="text-center p-4 bg-teal-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-2">{t('analytics.currentBalance')}</p>
              <p className="text-3xl font-bold text-teal-600">{analytics.current_balance}</p>
            </div>
          </div>
        </Card>

        {/* Monthly Trend */}
        <Card className="p-6">
          <h3 className="text-xl font-bold mb-6">{t('analytics.activityTrend')}</h3>
          <div className="space-y-4">
            {(analytics.monthly_trend || []).map((month, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="w-24 text-sm text-gray-600">{month.month}</div>
                <div className="flex-1 bg-gray-100 rounded-full h-8 relative overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-teal-500 to-cyan-500 h-full rounded-full flex items-center px-3"
                    style={{ width: `${Math.min((month.hours / 10) * 100, 100)}%` }}
                  >
                    <span className="text-white text-sm font-medium">
                      {month.hours}h ({month.transactions} {t('analytics.exchanges')})
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Tabs for Leaderboard and More */}
        <Tabs defaultValue="leaderboard" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="leaderboard">{t('analytics.leaderboard')}</TabsTrigger>
            <TabsTrigger value="impact">{t('analytics.impact')}</TabsTrigger>
          </TabsList>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <Trophy className="h-6 w-6 text-yellow-500" />
                <h3 className="text-xl font-bold">{t('analytics.topContributors')}</h3>
              </div>

              <div className="flex gap-2 mb-6">
                <button
                  onClick={() => fetchLeaderboard('hours_donated')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    leaderboardCategory === 'hours_donated'
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('analytics.hoursDonated')}
                </button>
                <button
                  onClick={() => fetchLeaderboard('donations')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    leaderboardCategory === 'donations'
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('analytics.creditsDonated')}
                </button>
                <button
                  onClick={() => fetchLeaderboard('rating')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${
                    leaderboardCategory === 'rating'
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t('analytics.topRated')}
                </button>
              </div>

              <div className="space-y-3">
                {leaderboard.map((entry, idx) => (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-4 p-4 rounded-lg ${
                      entry.user_id === user.id ? 'bg-teal-50 border-2 border-teal-500' : 'bg-gray-50'
                    }`}
                  >
                    <div className="text-2xl font-bold text-gray-400 w-8">#{entry.rank}</div>
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={entry.picture} />
                      <AvatarFallback className="bg-teal-100 text-teal-600">
                        {entry.name.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{entry.name}</p>
                      <p className="text-sm text-gray-500">@{entry.username}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-teal-600">{entry.value}</p>
                      <p className="text-xs text-gray-500">{entry.metric}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Impact Tab */}
          <TabsContent value="impact" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-xl font-bold mb-6">{t('analytics.yourImpact')}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="text-center p-6 bg-gradient-to-br from-teal-50 to-cyan-50 rounded-lg">
                  <Users className="h-12 w-12 mx-auto mb-3 text-teal-500" />
                  <p className="text-4xl font-bold text-teal-600 mb-2">{analytics.people_helped}</p>
                  <p className="text-gray-600">{t('analytics.peopleHelped')}</p>
                </div>
                
                <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg">
                  <Gift className="h-12 w-12 mx-auto mb-3 text-purple-500" />
                  <p className="text-4xl font-bold text-purple-600 mb-2">{analytics.donations_made}</p>
                  <p className="text-gray-600">{t('analytics.creditsDonatedTotal')}</p>
                </div>
              </div>

              <div className="mt-6 p-6 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg">
                <h4 className="text-lg font-bold mb-2">{t('analytics.communityImpact')}</h4>
                <p className="text-teal-100">
                  {t('analytics.impactMessage', { 
                    hours: analytics.hours_donated,
                    people: analytics.people_helped
                  })}
                </p>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
